import { c as _c } from "react/compiler-runtime";
import { Check, Copy, Expand, Minus, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { MermaidRenderer } from './mermaid-renderer';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { BetterTooltip } from '@/components/ui/tooltip';
type ZoomableMermaidProps = {
  content: string | undefined;
  className?: string;
  isLoading?: boolean;
  onError?: () => void;
  hideOnError?: boolean;
};
export function ZoomableMermaid(t0) {
  const $ = _c(41);
  const {
    content,
    className,
    isLoading,
    onError,
    hideOnError
  } = t0;
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  let t1;
  if ($[0] !== content) {
    t1 = async e => {
      e.stopPropagation();
      if (!content) {
        return;
      }
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };
    $[0] = content;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleCopy = t1;
  let t2;
  let t3;
  if ($[2] !== open) {
    t2 = () => {
      if (!open) {
        return;
      }
      const handleKeyDown = e_0 => {
        if (e_0.key === "Escape") {
          e_0.stopPropagation();
          e_0.preventDefault();
          setOpen(false);
        }
      };
      document.addEventListener("keydown", handleKeyDown, true);
      return () => {
        document.removeEventListener("keydown", handleKeyDown, true);
      };
    };
    t3 = [open];
    $[2] = open;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  if (!content && !isLoading) {
    return null;
  }
  if (isLoading || !content?.trim()) {
    const t4 = `flex items-center justify-center rounded-md border border-border bg-muted/20 min-h-[200px] w-full ${className || ""}`;
    let t5;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="flex flex-col items-center gap-3"><Spinner size="sm" /><span className="text-sm text-muted-foreground">Generating diagram...</span></div>;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    let t6;
    if ($[6] !== t4) {
      t6 = <div className={t4}>{t5}</div>;
      $[6] = t4;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    return t6;
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setOpen(true);
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== className || $[10] !== content || $[11] !== hideOnError || $[12] !== onError) {
    t5 = <div className="cursor-zoom-in"><MermaidRenderer content={content} className={className} onError={onError} hideOnError={hideOnError} /></div>;
    $[9] = className;
    $[10] = content;
    $[11] = hideOnError;
    $[12] = onError;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const t6 = copied ? "Copied!" : "Copy diagram";
  let t7;
  if ($[14] !== copied) {
    t7 = copied ? <Check className="h-4 w-4 text-muted-foreground" /> : <Copy className="h-4 w-4 text-muted-foreground" />;
    $[14] = copied;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== handleCopy || $[17] !== t7) {
    t8 = <Button variant="ghost" size="icon" className="size-7 rounded-sm opacity-70 transition-opacity hover:opacity-100" onClick={handleCopy} aria-label="Copy diagram">{t7}</Button>;
    $[16] = handleCopy;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t6 || $[20] !== t8) {
    t9 = <BetterTooltip content={t6}>{t8}</BetterTooltip>;
    $[19] = t6;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = e_1 => {
      e_1.stopPropagation();
      setOpen(true);
    };
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <BetterTooltip content="Expand diagram"><Button variant="ghost" size="icon" className="size-7 rounded-sm opacity-70 transition-opacity hover:opacity-100" onClick={t10} aria-label="Expand diagram"><Expand className="h-4 w-4 text-muted-foreground" /></Button></BetterTooltip>;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t9) {
    t12 = <div className="absolute top-1 right-1 flex gap-1">{t9}{t11}</div>;
    $[24] = t9;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t12 || $[27] !== t5) {
    t13 = <div className="relative group w-full" onClick={t4}>{t5}{t12}</div>;
    $[26] = t12;
    $[27] = t5;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <DialogTitle className="sr-only">Diagram preview</DialogTitle>;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      step: 0.1
    };
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== content || $[32] !== hideOnError || $[33] !== onError) {
    t16 = <DialogContent className="flex h-[90vh] w-[90vw] max-w-[90vw] max-h-[90vh] items-center justify-center sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2">{t14}<TransformWrapper initialScale={1} minScale={0.5} maxScale={5} wheel={t15}>{t17 => {
          const {
            zoomIn,
            zoomOut
          } = t17;
          return <div className="relative h-full w-full"><TransformComponent wrapperClass="h-full w-full cursor-grab active:cursor-grabbing" wrapperStyle={{
              width: "100%",
              height: "100%"
            }} contentClass="w-full h-full" contentStyle={{
              width: "100%",
              height: "100%"
            }}><MermaidRenderer content={content} className="w-full h-full max-w-none border-0 p-0 [&_svg]:h-full [&_svg]:w-full" onError={onError} hideOnError={hideOnError} /></TransformComponent><div className="absolute bottom-0 left-0 flex flex-col gap-2"><BetterTooltip content="Zoom in"><Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm opacity-70 transition-opacity hover:opacity-100" onClick={() => zoomIn()} aria-label="Zoom in"><Plus className="h-4 w-4" /></Button></BetterTooltip><BetterTooltip content="Zoom out"><Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm opacity-70 transition-opacity hover:opacity-100" onClick={() => zoomOut()} aria-label="Zoom out"><Minus className="h-4 w-4" /></Button></BetterTooltip></div></div>;
        }}</TransformWrapper></DialogContent>;
    $[31] = content;
    $[32] = hideOnError;
    $[33] = onError;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== open || $[36] !== t16) {
    t17 = <Dialog open={open} onOpenChange={setOpen}>{t16}</Dialog>;
    $[35] = open;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t13 || $[39] !== t17) {
    t18 = <>{t13}{t17}</>;
    $[38] = t13;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  return t18;
}